import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Text } from '@chakra-ui/react';
import {
  setCurrentStep,
  useAppDispatch,
  useAppSelector,
  useLazyGetScheduleTableDataQuery,
} from '@spartalabs/pdc-core';
import { Unauthorized, FowardHeader, Container } from 'components';
import TableCard from 'components/Cards/TableCard';
import { useCustomToast } from 'hooks';
import useAnalyticsEvent from 'hooks/useAnalyticsEvent';
import { ModelFilters, GA_NEW_SCHEDULE_LABEL } from 'utils/constants';
import { checkDateRange, downloadFile } from 'utils/functions';
import { CancelButton } from '../CancelButton';
import { CancelModal } from '../Modals/CancelModal';

export const HomeSchedules = () => {
  const globalDate = useAppSelector(state => state.globalDataSlicer.globalDate);
  const permissions = useAppSelector(
    state => state.authSlicer.user.permissions,
  );
  const { handleToast } = useCustomToast();

  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const analyticsEvent = useAnalyticsEvent({
    category: 'User',
    action: GA_NEW_SCHEDULE_LABEL,
  });

  const [showModal, setShowModal] = useState<{
    show: boolean;
    id: string;
    status?: string;
  }>({ show: false, id: '', status: '' });
  const [dateRange, setDateRange] = useState(checkDateRange(globalDate));

  const [
    fetchTable,
    {
      data: tableData,
      isLoading: isLoadingTable,
      isFetching: isFetchingTable,
      isError: isErrorTable,
    },
  ] = useLazyGetScheduleTableDataQuery();

  useEffect(() => {
    setDateRange(globalDate);
  }, [globalDate]);

  useEffect(() => {
    setCurrentStep(1);
  }, []);

  const handleCancel = () => {
    setShowModal({ show: false, id: '' });
  };

  const handleNavigateNewSchecule = () => {
    if (!permissions?.includes('createSchedule'))
      return handleToast({
        step: 4,
        action: 'acessar a pagina',
        helpText: 'Sua conta não possui permissão para essa seção',
      });

    analyticsEvent(`${GA_NEW_SCHEDULE_LABEL} Iniciado`);
    dispatch(setCurrentStep(1));
    navigate('/novo-agendamento/veiculos');
  };

  const tableLabels: TableLabel[] = useMemo(
    () => [
      {
        title: 'Placa',
        type: 'text',
        accessor: 'plate',
        disableSorting: true,
      },
      {
        title: 'Nº de atendimento',
        type: 'text',
        accessor: 'id',
        disableSorting: true,
      },
      {
        title: 'Status Atendimento',
        type: 'text',
        accessor: 'serviceStatus',
        disableSorting: true,
      },
      {
        title: 'Data Abertura',
        type: 'year',
        accessor: 'openDate',
        disableSorting: true,
      },
      {
        title: 'Data de Agendamento',
        type: 'text',
        accessor: 'scheduleDate',
        disableSorting: true,
      },
      {
        title: 'Data Reagendamento',
        type: 'text',
        accessor: 'rescheduleDate',
        disableSorting: true,
      },
      {
        title: 'Cancelar',
        type: 'custom',
        component: id => <CancelButton setShowModal={setShowModal} id={id} />,
      },
    ],
    [],
  );

  if (!permissions?.includes('scheduleList')) return <Unauthorized />;

  return (
    <Container mt="0px" pt="0px">
      <CancelModal
        id={showModal.id}
        obj={tableData}
        isOpen={showModal.show}
        onClose={handleCancel}
      />

      <FowardHeader
        title="Solicite um Agendamento de Manutenção"
        actionText="Novo Agendamento"
        navigateTo={handleNavigateNewSchecule}
      />

      <TableCard
        hasActions
        loading={isLoadingTable || isFetchingTable}
        fetchData={fetchTable}
        fetchDownload={async () =>
          await downloadFile('schedule', 'Agendamentos')
        }
        hasGlobalSearch
        customTitle={
          <Text
            alignSelf="center"
            color="text.100"
            fontSize="16px"
            fontWeight={400}
          >
            Relatórios de<b> todos os veículos</b>
          </Text>
        }
        subtitle="Acompanhe todos os detalhes da sua frota"
        overflowX="auto"
        w="100%"
        filters={ModelFilters}
        labels={tableLabels}
        data={tableData}
        setDateRange={setDateRange}
        initDate={dateRange}
        isError={isErrorTable}
        refetch={fetchTable}
      />
    </Container>
  );
};
